/* eslint-disable no-underscore-dangle */
/* eslint-disable camelcase */
import React, { useState, useContext, ReactNode } from 'react';
import { withRouter, RouteComponentProps, Link, useRouteMatch } from 'react-router-dom';
import { Input, DatePicker, message, Row, Col, Select, Modal, Popover } from 'antd';
import { SearchOutlined, FileExcelOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { v4 as uuidv4 } from 'uuid';
import { useQuery } from '@apollo/react-hooks';
import { formatDateTime, formatDate } from 'shared/utils/dateTime';
import formatCurrency from 'shared/utils/formatCurrency';
import baseUrl from 'shared/config/baseUrl';
import COD_PARCELS_QUERY, {
  CodParcelsData,
  CodParcelsVars,
  CodParcels,
} from 'shared/graphql/query/codParcels';
import {
  CodContext,
  SelectedCodDetailType,
} from 'views/CashOnDelivery/Routes/CashOnDeliveryByParcel/common/context/CodContext';
import { Text, Button, SelectVendorsField, TableSearch } from 'shared/components';
import { CodParcelStatus } from 'views/CashOnDelivery/Routes/CashOnDeliveryByParcel/common/model/codParcelStatus.model';
import { ToolsContainer } from './Styles';

const { Option } = Select;
const { RangePicker } = DatePicker;
const { confirm } = Modal;

interface CodListTableProps extends RouteComponentProps {
  status: CodParcelStatus;
  pageSize: number;
  onChangePageSize: (pageSize: number) => void;
  currentPage: number;
  onChangeCurrentPage: (currentPage: number) => void;
}

type CodListDataTable = {
  key: number;
  tools: ReactNode;
  column1: string;
  column2: string;
  column3: string;
  column4: string;
  column5: ReactNode;
  column6: ReactNode;
  column7: string;
  column8: string;
  column9: string;
  column10: string;
};

const CodListTable: React.FC<CodListTableProps> = (props) => {
  const { status, pageSize, onChangePageSize, currentPage, onChangeCurrentPage } = props;
  const [codInDate, setCodInDate] = useState<string>('');
  const [codInNumber, setCodInNumber] = useState<string>('');
  const [dueDate, setDueDate] = useState<string>('');
  const [deliveredDate, setDeliveredDate] = useState<string>('');
  const [trackingNumber, setTrackingNumber] = useState<string>('');
  const [paidChannel, setPaidChannel] = useState<string>('');
  const [vendorId, setVendorId] = useState<string>('');
  const [orderBy, setOrderBy] = useState<string>('');
  const [orderType, setOrderType] = useState<'1' | '-1' | ''>('');
  const { path } = useRouteMatch();
  const { setSelectedCodDetail } = useContext(CodContext);

  const { loading, error, data } = useQuery<CodParcelsData, CodParcelsVars>(COD_PARCELS_QUERY, {
    variables: {
      cod_status: status,
      cod_in_date: codInDate,
      cod_in_number: codInNumber,
      due_date: dueDate,
      delivered_date: deliveredDate,
      tracking_number: trackingNumber,
      paid_channel: paidChannel,
      vendor: vendorId,
      order_by: orderBy,
      order_type: orderType,
      page: currentPage,
      showData: pageSize,
    },
    fetchPolicy: 'cache-and-network',
  });

  const showConfirmDownloadFile = (codInId: string) => {
    const modal = {
      title: 'ยืนยัน',
      icon: <ExclamationCircleOutlined />,
      content: (
        <>
          ต้องการ <Text strong>ดาวน์โหลดไฟล์</Text> ใช่หรือไม่?
        </>
      ),
      okText: 'ดาวน์โหลด',
      onOk() {
        window.location.href = `${baseUrl}/cod/CodParcelExcel/${codInId}`;
      },
      cancelText: 'ยกเลิก',
    };

    confirm(modal);
  };

  const contentPopOver = (value: CodParcels['parcel_orders']) => {
    return (
      <div style={{ width: 450 }}>
        <div style={{ maxHeight: 200, overflow: 'auto' }}>
          <div style={{ marginLeft: 10, marginRight: 10 }}>
            <Row gutter={[8, 0]} style={{ fontSize: 16 }}>
              <Col span={8}>
                <Text strong>Tracking Number</Text>
              </Col>
              <Col span={8}>
                <Text strong>Delivered Date</Text>
              </Col>
              <Col span={8} style={{ textAlign: 'right' }}>
                <Text strong>COD Value</Text>
              </Col>
            </Row>
            {value.map((item) => (
              <Row gutter={[8, 0]} key={uuidv4()} style={{ fontSize: 16 }}>
                <Col span={8}>
                  <Text>{item.tracking_number || '-'}</Text>
                </Col>
                <Col span={8}>
                  <Text>{item.delivered_date ? formatDateTime(item.delivered_date) : '-'}</Text>
                </Col>
                <Col span={8} style={{ textAlign: 'right' }}>
                  <Text>{formatCurrency(item.cod_price) || '-'}</Text>
                </Col>
              </Row>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const popOver = (value: CodParcels['parcel_orders']) => {
    if (value.length === 1) {
      return <Text>{value[0].tracking_number}</Text>;
    }
    if (value.length > 1) {
      return (
        <Popover placement="bottom" content={contentPopOver(value)}>
          <div style={{ display: 'inline' }}>
            <Text strong customType="primary">
              {value.length} Tracking Number
            </Text>
          </div>
        </Popover>
      );
    }
    return '-';
  };

  const columns = [
    {
      title: 'เครื่องมือ',
      dataIndex: 'tools',
      width: 80,
    },
    ...(status !== CodParcelStatus.WAITING3PL
      ? [
          {
            title: 'วันที่แจ้งโอน',
            dataIndex: 'column1',
            width: 250,
            search: (
              <RangePicker
                onChange={(values) => {
                  const formatRangDate =
                    values === null
                      ? ''
                      : `${values[0]!.format('YYYY-MM-DD')}:${values[1]!.format('YYYY-MM-DD')}`;

                  setCodInDate(formatRangDate);
                }}
                format="YYYY/MM/DD"
                placeholder={['เริ่มต้น', 'สิ้นสุด']}
              />
            ),
          },
        ]
      : []),
    {
      title: 'รหัส COD-IN',
      dataIndex: 'column2',
      sortName: 'cod_in_number',
      width: 180,
      search: (
        <Input
          allowClear
          value={codInNumber}
          placeholder="ค้นหา"
          onChange={(event) => setCodInNumber(event.target.value)}
        />
      ),
    },
    {
      title: 'วันที่ต้องทำจ่าย',
      dataIndex: 'column3',
      width: 250,
      search: (
        <RangePicker
          onChange={(values) => {
            const formatRangDate =
              values === null
                ? ''
                : `${values[0]!.format('YYYY-MM-DD')}:${values[1]!.format('YYYY-MM-DD')}`;

            setDueDate(formatRangDate);
          }}
          format="YYYY/MM/DD"
          placeholder={['เริ่มต้น', 'สิ้นสุด']}
        />
      ),
    },
    {
      title: 'Delivered Date',
      dataIndex: 'column4',
      width: 250,
      search: (
        <RangePicker
          onChange={(values) => {
            const formatRangDate =
              values === null
                ? ''
                : `${values[0]!.format('YYYY-MM-DD')}:${values[1]!.format('YYYY-MM-DD')}`;

            setDeliveredDate(formatRangDate);
          }}
          format="YYYY/MM/DD"
          placeholder={['เริ่มต้น', 'สิ้นสุด']}
        />
      ),
    },
    {
      title: 'Tracking Number',
      dataIndex: 'column5',
      width: 250,
      search: (
        <Input
          allowClear
          value={trackingNumber}
          placeholder="ค้นหา"
          onChange={(event) => setTrackingNumber(event.target.value)}
        />
      ),
    },
    {
      title: 'มูลค่า COD-IN',
      dataIndex: 'column6',
      sortName: 'total_cod',
      width: 150,
      align: 'right',
    },
    {
      title: 'Channel',
      dataIndex: 'column7',
      width: 150,
      search: (
        <Select
          onChange={(value) => {
            setPaidChannel(value);
          }}
          value={paidChannel}
          style={{ width: '100%' }}
        >
          <Option value="">ทั้งหมด</Option>
          <Option value="Direct QR">Direct QR</Option>
          <Option value="Agent">Agent</Option>
        </Select>
      ),
    },
    {
      title: 'ชื่อ 3PL',
      dataIndex: 'column8',
      width: 300,
      search: <SelectVendorsField onChange={(value: string) => setVendorId(value)} />,
    },
    {
      title: 'เบอร์โทรศัพท์',
      dataIndex: 'column9',
      width: 180,
    },
    {
      title: 'อัพเดทล่าสุด',
      dataIndex: 'column10',
      sortName: 'last_update',
      width: 180,
    },
  ];

  let codListData: CodListDataTable[] = [];
  let totalDocument = 0;

  if (data?.codParcels) {
    const { codParcels } = data.codParcels;
    codListData = codParcels.map(
      (item, index): CodListDataTable => ({
        key: index,
        tools: (
          <ToolsContainer>
            <Link to={`${path}/detail/${item._id}`}>
              <Button
                onClick={() => {
                  setSelectedCodDetail(
                    (prevState): SelectedCodDetailType => ({
                      ...prevState,
                      codId: item._id,
                      codStatus: item.cod_status,
                      codNumber: item.cod_in_number,
                    }),
                  );
                }}
                color="grey--text-only"
                icon={<SearchOutlined />}
                size="small"
              />
            </Link>
            {(status === CodParcelStatus.WAITING3PL || status === CodParcelStatus.PENDING) && (
              <Button
                onClick={() => showConfirmDownloadFile(item._id)}
                color="grey--text-only"
                icon={<FileExcelOutlined />}
                size="small"
              />
            )}
          </ToolsContainer>
        ),
        column1: item.cod_in_date ? formatDateTime(item.cod_in_date) : '-',
        column2: item.cod_in_number || '-',
        column3: item.due_date ? formatDate(item.due_date) : '-',
        column4: item.delivered_date ? formatDateTime(item.delivered_date) : '-',
        column5: popOver(item.parcel_orders),
        column6: (
          <Text strong customType="success">
            {formatCurrency(item.total_cod)}
          </Text>
        ),
        column7: item.paid_channel || '-',
        column8: item.vendor?.full_name || '-',
        column9: item.vendor?.phone.length > 0 ? item.vendor.phone.join(', ') : '-',
        column10: item.last_update ? formatDateTime(item.last_update) : '-',
      }),
    );
    totalDocument = data.codParcels.totalDocument;
  }

  if (error) {
    message.error(error.message);
  }

  return (
    <>
      <Row
        align="middle"
        justify="space-between"
        style={{ borderBottom: '1px solid #e3e3e3', height: 60 }}
      >
        <Col>
          <Select defaultValue="all" style={{ width: 180 }}>
            <Option value="all">รายการทั้งหมด ({totalDocument})</Option>
          </Select>
        </Col>
        <Col>
          แสดง{' '}
          <Select
            onChange={(value) => {
              onChangeCurrentPage(1);
              onChangePageSize(value);
            }}
            value={pageSize}
            style={{ width: 80 }}
          >
            <Option value={10}>10</Option>
            <Option value={20}>20</Option>
            <Option value={30}>30</Option>
          </Select>
        </Col>
      </Row>

      <TableSearch
        columns={columns}
        dataSource={codListData}
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalDocument,
          showSizeChanger: false,
          onChange: (value: number) => onChangeCurrentPage(value),
        }}
        onSort={(sort) => {
          setOrderBy(sort.orderBy);
          setOrderType(sort.orderType);
        }}
      />
    </>
  );
};

export default withRouter(CodListTable);
